export interface PromptAnalysisResult {
  warnings: string[];
  suggestions: string[];
  detectedStyles: string[];
  complexityScore: number;
  estimatedTokens: number;
}

const STYLE_KEYWORDS: Record<string, string[]> = {
  cinematic: ['cinematic', 'film', 'anamorphic', 'widescreen'],
  noir: ['noir', 'black and white', 'monochrome'],
  realistic: ['realistic', 'photoreal', 'naturalistic'],
  fantasy: ['fantasy', 'magical', 'mythic', 'surreal'],
  cyberpunk: ['cyberpunk', 'neon', 'futuristic', 'dystopian'],
  anime: ['anime', 'manga', 'cel shaded', 'toon'],
};

const CONFLICTING_STYLE_PAIRS: Array<[string, string]> = [
  ['noir', 'vibrant'],
  ['black and white', 'saturated'],
  ['realistic', 'cartoon'],
  ['photorealistic', 'anime'],
  ['minimalist', 'hyper-detailed'],
];

const VFX_KEYWORDS = [
  'fog', 'haze', 'god rays', 'dust particles', 'bloom', 'smoke', 'glitch', 'motion blur',
  'lens flare', 'light streaks', 'chromatic aberration', 'explosion', 'fire', 'lightning',
  'energy aura', 'particles'
];

const ACTION_VERBS = [
  'running', 'walking', 'jumping', 'standing', 'sitting', 'dancing', 'fighting', 'driving',
  'holding', 'looking', 'smiling', 'flying', 'swimming', 'talking', 'posing'
];

const STOPWORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'to', 'of', 'in', 'on', 'with', 'for', 'at', 'from', 'by',
  'is', 'are', 'was', 'were', 'be', 'as', 'it', 'this', 'that', 'into', 'over', 'under'
]);

const normalize = (prompt: string): string => prompt.toLowerCase().replace(/\s+/g, ' ').trim();

const countOccurrences = (text: string, term: string): number => {
  if (!term) return 0;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`\\b${escaped}\\b`, 'g');
  return (text.match(regex) ?? []).length;
};

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

export const analyzePrompt = (prompt: string): PromptAnalysisResult => {
  const normalized = normalize(prompt);

  if (!normalized) {
    return {
      warnings: ['Prompt is empty.'],
      suggestions: ['Add a clear subject, action, and visual style.'],
      detectedStyles: [],
      complexityScore: 0,
      estimatedTokens: 0,
    };
  }

  const warnings: string[] = [];
  const suggestions: string[] = [];

  const detectedStyles = Object.entries(STYLE_KEYWORDS)
    .filter(([, keywords]) => keywords.some((keyword) => normalized.includes(keyword)))
    .map(([style]) => style);

  CONFLICTING_STYLE_PAIRS.forEach(([left, right]) => {
    if (normalized.includes(left) && normalized.includes(right)) {
      warnings.push(`Conflicting style instructions detected: "${left}" and "${right}".`);
    }
  });

  const vfxMentions = VFX_KEYWORDS.reduce((count, keyword) => count + countOccurrences(normalized, keyword), 0);
  if (vfxMentions > 6) {
    warnings.push('Prompt includes excessive VFX layers that may produce noisy or unstable outputs.');
    suggestions.push('Reduce VFX to the 2-5 most important effects for better control.');
  }

  const words = normalized.split(' ').filter(Boolean);
  const keywordCounts = new Map<string, number>();
  for (const word of words) {
    if (word.length < 3 || STOPWORDS.has(word)) continue;
    keywordCounts.set(word, (keywordCounts.get(word) ?? 0) + 1);
  }

  const redundantKeywords = Array.from(keywordCounts.entries())
    .filter(([, count]) => count >= 3)
    .map(([word]) => word);

  if (redundantKeywords.length > 0) {
    warnings.push(`Redundant keywords detected: ${redundantKeywords.join(', ')}.`);
    suggestions.push('Remove repetitive terms and keep only the strongest descriptors.');
  }

  if (words.length < 8 || /\b(scene|image|shot|visual)\b/.test(normalized)) {
    warnings.push('Scene description may be too vague.');
    suggestions.push('Add specific location, time-of-day, lighting, and mood details.');
  }

  const hasSubject = /\b(a|an|the)\s+[a-z]+/.test(normalized) || /\b(person|man|woman|child|subject|character|car|object)\b/.test(normalized);
  const hasAction = ACTION_VERBS.some((verb) => normalized.includes(verb));

  if (!hasSubject) {
    warnings.push('Subject is missing or unclear.');
    suggestions.push('Specify who or what should be the focal subject.');
  }

  if (!hasAction) {
    warnings.push('Action is missing or unclear.');
    suggestions.push('Include a clear action verb to define what is happening in the scene.');
  }

  if (detectedStyles.length === 0) {
    suggestions.push('Consider adding a style direction (e.g., cinematic, noir, realistic).');
  }

  const sentenceComplexity = Math.ceil(words.length / 6);
  const punctuationComplexity = (normalized.match(/[,;:]/g) ?? []).length;
  const complexityScore = clamp(
    sentenceComplexity * 4 +
      punctuationComplexity * 2 +
      detectedStyles.length * 6 +
      Math.min(vfxMentions, 10) * 4 -
      warnings.length * 2,
    0,
    100
  );

  const estimatedTokens = Math.ceil(normalized.length / 4);

  return {
    warnings,
    suggestions: Array.from(new Set(suggestions)),
    detectedStyles,
    complexityScore,
    estimatedTokens,
  };
};
